import type { Product } from '@/types';
import { getEffectiveDisplayPriceForCurrency } from '@/utils/currency';

type Pixel = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  push?: Pixel;
  loaded?: boolean;
  version?: string;
};
const PIXEL_ID = '1022688610761437';
let initialized = false;

function pixel(): Pixel | undefined {
  if (typeof window === 'undefined' || import.meta.env.DEV) return;
  const target = window as Window & { fbq?: Pixel; _fbq?: Pixel };
  if (!initialized) {
    if (!target.fbq) {
      const fbq = ((...args: unknown[]) => {
        if (fbq.callMethod) fbq.callMethod(...args);
        else fbq.queue.push(args);
      }) as Pixel;
      fbq.queue = [];
      fbq.push = fbq;
      fbq.loaded = true;
      fbq.version = '2.0';
      target.fbq = fbq;
      target._fbq = fbq;
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(script);
    }
    target.fbq('init', PIXEL_ID);
    initialized = true;
  }
  return target.fbq;
}

export function trackMetaEvent(name: string, data: Record<string, unknown> = {}, eventId?: string): boolean {
  try {
    const fbq = pixel();
    if (!fbq) return false;
    fbq('trackSingle', PIXEL_ID, name, data, ...(eventId ? [{ eventID: eventId }] : []));
    return true;
  } catch {
    // Tracking must never prevent shopping or payment confirmation.
    return false;
  }
}

export function trackMetaPageView(path: string): void {
  // Do not load the pixel on account or payment callback pages with private data.
  if (/^\/(?:login|register|auth|profile|checkout|payment|resultado|gracias|reset|forgot)/i.test(path)) return;
  trackMetaEvent('PageView');
}

export function trackMetaProduct(name: 'ViewContent' | 'AddToCart', product: Product, quantity = 1): void {
  const value = getEffectiveDisplayPriceForCurrency(product, 'PYG') * quantity;
  if (!Number.isFinite(value) || value < 0 || quantity <= 0) return;
  trackMetaEvent(name, {
    content_ids: [String(product.id)], content_type: 'product',
    contents: [{ id: String(product.id), quantity }], currency: 'PYG', value,
  });
}

export function trackMetaPurchase(payment: { pagado: boolean; cancelado: boolean; numero_pedido: string; monto: string }): void {
  const value = Number(payment.monto);
  if (!payment.pagado || payment.cancelado || !payment.numero_pedido || !Number.isFinite(value) || value <= 0) return;
  const key = `meta-purchase:${PIXEL_ID}:${payment.numero_pedido}`;
  try {
    if (localStorage.getItem(key)) return;
  } catch { /* Storage may be unavailable. */ }
  if (trackMetaEvent('Purchase', { value, currency: 'PYG' }, `purchase-${payment.numero_pedido}`)) {
    try { localStorage.setItem(key, '1'); } catch { /* Event ID also allows server deduplication. */ }
  }
}
